import { listOrgEnrollments } from "./enrollmentHelpers"
import { listCourses } from "./courseHelpers"
import { listOrgCertificates } from "./certHelpers"
import { listOrgVerificationEvents } from "./verificationEventHelpers"
import { listOrgOrders } from "./orderHelpers"
import { listOrgInvitations } from "./invitationHelpers"
import { listCohorts } from "./cohortHelpers"
import { dateKeyFor, toMillis } from "./dateRangeHelpers"

const formatCurrency = (amount, currency) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD", maximumFractionDigits: 0 }).format(amount || 0)

// Groups a list of {courseId, ts} pairs by course+day, producing one feed
// item per group — "3 learners enrolled in X" when count > 1, or a single
// named line when count === 1. Mirrors the spec's own "42 learners enrolled"
// phrasing rather than listing every individual record separately.
const groupByCourseDay = (records, courseTitleMap, verb) => {
  const groups = new Map()
  records?.forEach(({ courseId, ts }) => {
    const ms = toMillis(ts)
    if (ms === null) return
    const key = `${courseId}::${dateKeyFor(new Date(ms))}`
    const existing = groups.get(key) || { courseId, latestMs: ms, count: 0 }
    existing.count += 1
    existing.latestMs = Math.max(existing.latestMs, ms)
    groups.set(key, existing)
  })

  return Array.from(groups.values())?.map((g) => {
    const title = courseTitleMap?.get(g?.courseId) || "a course"
    const label = g?.count > 1 ? `${g?.count} learners ${verb} ${title}` : `A learner ${verb} ${title}`
    return { ts: g?.latestMs, label }
  })
}

export const computeActivityFeed = async (orgId, limit = 20) => {
  const [{ enrollments }, { courses }, { certificates }, { events: verifications }, { orders }, { invitations }, { cohorts }] = await Promise.all([
    listOrgEnrollments(orgId),
    listCourses(orgId),
    listOrgCertificates(orgId),
    listOrgVerificationEvents(orgId),
    listOrgOrders(orgId),
    listOrgInvitations(orgId),
    listCohorts(orgId),
  ])

  const courseTitleMap = new Map(courses?.map((c) => [c?.courseId, c?.title]))
  const certByCertId = new Map(certificates?.map((c) => [c?.certId, c]))

  const items = []

  // Enrollments — grouped per course/day
  const enrollmentRecords = enrollments?.map((e) => ({ courseId: e?.courseId, ts: e?.enrolledAt })) || []
  groupByCourseDay(enrollmentRecords, courseTitleMap, "enrolled in")?.forEach((g) => {
    items.push({ type: "enrollment", ts: g?.ts, title: g?.label })
  })

  // Course completions — grouped per course/day, approximated via lastActivityAt
  const completionRecords =
    enrollments
      ?.filter((e) => (e?.progressPercent || 0) >= 100)
      ?.map((e) => ({ courseId: e?.courseId, ts: e?.lastActivityAt })) || []
  groupByCourseDay(completionRecords, courseTitleMap, "completed")?.forEach((g) => {
    items.push({ type: "completion", ts: g?.ts, title: g?.label })
  })

  // Credentials earned — real display name, real course title
  certificates?.forEach((c) => {
    const ms = toMillis(c?.issueDate)
    if (ms === null) return
    items.push({
      type: "credential_issued",
      ts: ms,
      title: `${c?.issuedTo || "A learner"} earned a credential for ${c?.courseTitle || "a course"}`,
    })
  })

  // Credential verifications — real external check events
  verifications?.forEach((v) => {
    const ms = toMillis(v?.createdAt)
    if (ms === null) return
    const cert = certByCertId?.get(v?.certId)
    items.push({
      type: "credential_verified",
      ts: ms,
      title: `Credential #${v?.certId || "unknown"} was verified${cert?.courseTitle ? ` (${cert.courseTitle})` : ""}`,
    })
  })

  // Purchases — real paid orders only
  orders
    ?.filter((o) => o?.status === "paid")
    ?.forEach((o) => {
      const ms = toMillis(o?.paidAt || o?.createdAt)
      if (ms === null) return
      items.push({
        type: "purchase",
        ts: ms,
        title: `${o?.customerName || "A customer"} purchased ${o?.productTitle || "a product"} — ${formatCurrency(o?.amount, o?.currency)}`,
      })
    })

    // Invitations sent â€” named recipients only (shareable links have no
  // specific person to name in the feed, so they're intentionally excluded
  // here even though they still count toward the Invitations metrics strip).
  invitations
    ?.filter((i) => !!i?.email)
    ?.forEach((i) => {
      const ms = toMillis(i?.createdAt)
      if (ms === null) return
      const forWhat = i?.programTitle || i?.courseTitle || "a course"
      items.push({
        type: "invitation_sent",
        ts: ms,
        title: `${i.email} was invited to ${forWhat}${i?.cohortName ? ` (${i.cohortName})` : ""}`,
      })
    })

  // Invitations accepted â€” a real conversion signal, distinct from the send event above
  invitations
    ?.filter((i) => i?.status === "accepted" && !!i?.acceptedAt)
    ?.forEach((i) => {
      const ms = toMillis(i?.acceptedAt)
      if (ms === null) return
      const forWhat = i?.programTitle || i?.courseTitle || "a course"
      items.push({
        type: "invitation_accepted",
        ts: ms,
        title: `${i?.email || "A learner"} accepted their invitation to ${forWhat}`,
      })
    })

  // Cohorts created
  cohorts?.forEach((c) => {
    const ms = toMillis(c?.createdAt)
    if (ms === null) return
    items.push({
      type: "cohort_created",
      ts: ms,
      title: `Cohort "${c?.name || "Untitled"}" was created`,
    })
  })

  return items?.sort((a, b) => b?.ts - a?.ts)?.slice(0, limit)
}